import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { acceptHandoffToken } from "./auth/handoff";
import { isTokenValid } from "./auth/token";
import { AppShell } from "./layout/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { InPlayPage } from "./pages/InPlayPage";
import { PlayerProfilePage } from "./pages/PlayerProfilePage";
import { PlayerTripPage } from "./pages/PlayerTripPage";
import { PlayersPage } from "./pages/PlayersPage";
import { NotAuthorizedPage, NotFoundPage, NotificationTriggerPage } from "./pages/StatusPages";
import { RouteError } from "./pages/RouteError";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

// OUTSIDE THE SHELL, AS IN ANGULAR: THE STATUS PAGES AND THE NATIVE APPS' NOTIFICATION TAP
const notAuthorizedRoute = createRoute({ getParentRoute: () => rootRoute, path: "/not-authorized", component: NotAuthorizedPage });
const notFoundRoute = createRoute({ getParentRoute: () => rootRoute, path: "/not-found", component: NotFoundPage });
const notificationTriggerRoute = createRoute({ getParentRoute: () => rootRoute, path: "/tabs/active", component: NotificationTriggerPage });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// PATHLESS LAYOUT ROUTE: THE SHELL AND THE AUTH CHECK, SHARED BY EVERY SIGNED-IN
// PAGE. MOUNTED ONCE, UNLIKE THE ANGULAR APP WHERE EACH TOP-LEVEL ROUTE DECLARES
// ITS OWN `AppLayoutComponent` AND REBUILDS THE SHELL PER NAVIGATION.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: ({ location, context }) => {
    acceptHandoffToken(location.searchStr, context.queryClient);
    if (!isTokenValid()) {
      throw redirect({ to: "/login" });
    }
  },
  component: AppShell,
});

// DEEP LINKS PRESERVED VERBATIM FROM `app-routing.module.ts` / `in-play-routing.module.ts`
// CODE-SPLIT: THE CHARTS LIBRARY ONLY LOADS WHEN THE DASHBOARD IS OPENED, SO A
// NATIVE-SHELL DEEP LINK STRAIGHT TO A PLAYER DOES NOT PAY FOR IT
const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./pages/DashboardPage"), "DashboardPage"),
});
// `?userId=` IS THE DASHBOARD'S HOST-TILE LINK. ANGULAR PASSES IT AS A MATRIX
// PARAM (`/in-play;userId=5`), WHICH TANSTACK ROUTER HAS NO EQUIVALENT FOR; IT IS
// AN IN-APP LINK ONLY, SO NO NATIVE SHELL DEPENDS ON ITS SHAPE.
const inPlayRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/in-play",
  validateSearch: (search: Record<string, unknown>): { userId?: number } => {
    const userId = Number(search["userId"]);
    return Number.isInteger(userId) && userId > 0 ? { userId } : {};
  },
  component: InPlayPage,
});
const inPlayProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/in-play/$playerId",
  component: PlayerProfilePage,
});
// A TRIP'S SESSIONS (ANGULAR'S `:id/:trip_id` UNDER BOTH `in-play` AND `players`)
const inPlayTripRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/in-play/$playerId/$tripId",
  component: PlayerTripPage,
});
const dailyTasksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/daily-tasks",
  component: lazyRouteComponent(() => import("./pages/DailyTasksPage"), "DailyTasksPage"),
});
// CODE-SPLIT FOR THE SAME REASON AS THE DASHBOARD: FULLCALENDAR IS HEAVY. LIKE
// ANGULAR, THE ROUTE ITSELF IS NOT ROLE-GATED; THE MENU HIDES IT FROM MANAGERS.
const calendarRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/calendar",
  component: lazyRouteComponent(() => import("./pages/CalendarPage"), "CalendarPage"),
});
const playersRoute = createRoute({ getParentRoute: () => authedRoute, path: "/players", component: PlayersPage });
const playerProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/players/$playerId",
  component: PlayerProfilePage,
});

const playerTripRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/players/$playerId/$tripId",
  component: PlayerTripPage,
});

// THE REST OF THE APP'S PAGES, EACH CODE-SPLIT (WRITTEN OUT SO THE PATHS STAY TYPED)
const eventsManagerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events-manager",
  component: lazyRouteComponent(() => import("./pages/EventsManagerPage"), "EventsManagerPage"),
});
const eventDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events-manager/$eventId",
  component: lazyRouteComponent(() => import("./pages/EventDetailPage"), "EventDetailPage"),
});
const eventBookingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events-manager/$eventId/booking",
  component: lazyRouteComponent(() => import("./pages/EventBookingPage"), "EventBookingPage"),
});
const eventRedemptionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events-manager/$eventId/redemption",
  component: lazyRouteComponent(() => import("./pages/EventRedemptionPage"), "EventRedemptionPage"),
});
const eventAnalysisRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/events-manager/$eventId/analysis",
  component: lazyRouteComponent(() => import("./pages/EventAnalysisPage"), "EventAnalysisPage"),
});
// `?card=` OPENS A PLAYER (THE PROFILE'S CONTACT BUTTON)
const playerCommunicationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/player-communications",
  validateSearch: (search: Record<string, unknown>): { card?: string } => (typeof search["card"] === "string" && search["card"] ? { card: search["card"] } : {}),
  component: lazyRouteComponent(() => import("./pages/PlayerCommunicationsPage"), "PlayerCommunicationsPage"),
});
const opportunityCenterRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/opportunity-center",
  component: lazyRouteComponent(() => import("./pages/OpportunityCenterPage"), "OpportunityCenterPage"),
});
const hostActivityViewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/host-activity-view",
  component: lazyRouteComponent(() => import("./pages/HostActivityViewPage"), "HostActivityViewPage"),
});
const notificationRulesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/notification-rules",
  component: lazyRouteComponent(() => import("./pages/NotificationRulesPage"), "NotificationRulesPage"),
});
const notificationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/notifications",
  component: lazyRouteComponent(() => import("./pages/NotificationsPage"), "NotificationsPage"),
});
const reportingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/reporting",
  component: lazyRouteComponent(() => import("./pages/ReportingPage"), "ReportingPage"),
});
const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("./pages/SettingsPage"), "SettingsPage"),
});
const floorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/floor",
  component: lazyRouteComponent(() => import("./pages/FloorPage"), "FloorPage"),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  notAuthorizedRoute,
  notFoundRoute,
  notificationTriggerRoute,
  authedRoute.addChildren([
    eventsManagerRoute,
    eventDetailRoute,
    eventBookingRoute,
    eventRedemptionRoute,
    eventAnalysisRoute,
    playerCommunicationsRoute,
    opportunityCenterRoute,
    hostActivityViewRoute,
    notificationRulesRoute,
    notificationsRoute,
    reportingRoute,
    settingsRoute,
    floorRoute,dashboardRoute, dailyTasksRoute, calendarRoute, inPlayRoute, inPlayProfileRoute, inPlayTripRoute, playersRoute, playerProfileRoute, playerTripRoute]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: false, defaultErrorComponent: RouteError });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
  // THE PLAYER A PROFILE NAVIGATION MEANT TO OPEN - SEE `PlayerLookup` IN `api/queries.ts`
  interface HistoryState {
    expectedCmsPlayerId?: string;
    // THE TRIP ROW A "SESSIONS" CLICK OPENED, FOR THE TRIP PAGE'S TILES (ANGULAR'S `state.data`)
    trip?: import("./players/profile-api").Trip;
  }
}
